import { z } from "zod";
// A text column of a table can offer a fixed list of values, entered as a
// dropdown instead of free text. The list lives on the template column as
// `text_options` (text[], NULL for free text). Only plain text
// columns take one: not a computed column, and not the index column, whose
// values are stored apart from the row data.
/** The list as a template column carries it: trimmed, non-empty, unique. */
export const columnTextOptionsSchema = z
    .array(z.string().trim().min(1, "An option cannot be blank"))
    .min(1, "A dropdown needs at least one option")
    .refine((options) => new Set(options).size === options.length, {
    message: "Options must be unique",
})
    .nullable();
/** Whether a column of this shape may carry options at all. */
export function canHaveTextOptions(column) {
    return (column.data_type === "string" && !column.is_index && !column.is_computed);
}
/**
 * The options a column offers, or null for free text. Reads the jsonb value
 * defensively, and a column that cannot carry options never reports any, so
 * saving through this drops a stale list from a column switched to number,
 * computed or index instead of tripping the database check.
 */
export function columnTextOptions(column) {
    if (!canHaveTextOptions(column))
        return null;
    const parsed = z.array(z.string()).safeParse(column.text_options);
    return parsed.success && parsed.data.length > 0 ? parsed.data : null;
}
/**
 * The cells in a row's data that hold a value outside their column's options.
 * Row data is keyed by column name. Blank cells are allowed: whether a value is
 * required is a separate rule. A cell holding the same value as `previous` is
 * not checked, so a value recorded before an option was removed stays editable
 * around rather than locking its whole row.
 */
export function invalidOptionCells(columns, data, previous) {
    const invalid = [];
    for (const column of columns) {
        const options = columnTextOptions(column);
        if (!options)
            continue;
        const value = data[column.name];
        if (value == null || value === "")
            continue;
        if (previous && previous[column.name] === value)
            continue;
        if (typeof value !== "string" || !options.includes(value)) {
            invalid.push({ column: column.name, value: String(value), options });
        }
    }
    return invalid;
}
/**
 * The option a pasted or imported text stands for, matched ignoring case and
 * surrounding space: the option itself, null for a blank, or undefined when it
 * names none of them.
 */
export function matchTextOption(options, text) {
    const t = text.trim().toLowerCase();
    if (t === "")
        return null;
    return options.find((o) => o.toLowerCase() === t);
}
//# sourceMappingURL=tabular-column-options.js.map